/**
 * Data-driven mechanic dispatcher (rebuilt for the new MechanicEntry shape).
 *
 * Each card's `attackDefendFields.mechanics[]` entry has:
 *   - appliesWhen: "attack" | "defend" | "either" — gates on play mode
 *   - condition:   "none" | damage-* | block-fully-blocks — gates on combat
 *   - effects:     EffectRow[] — payload, dispatched via applyEffectRow()
 *
 * Two call sites in playtestEngine.ts:
 *   1) `playAttackDefend` — fires entries whose condition is "none"
 *   2) `resolveCombat`    — fires entries whose condition needs damage data
 *
 * Legacy shape (trigger/conditionType/effectType/effectAmount/effectScope)
 * is auto-migrated on read so old cards keep working until re-saved.
 */
import type {
  Card,
  EffectRow,
  MechanicCondition,
  MechanicEntry,
} from "@/types/card";
import type { PlayerState } from "@/types/game";
import {
  applyEffectRow,
  emptyResult,
  type EffectCtx,
  type EffectResult,
} from "./effects";
import { migrateAttackDefendFields } from "./cardMigration";

interface Ctx {
  self: PlayerState;
  opponent: PlayerState;
  cardName: string;
  fullBlock?: boolean;
  damageDealt?: number;
  damageTaken?: number;
  currentRound: number;
  /** Player number of the source (the one playing the card). Required for
   *  loadout effects that build a PendingPrompt addressed to the right
   *  player. Defaults to 1 when callers don't pass it. */
  selfNum?: 1 | 2;
}

export type MechanicResult = EffectResult;

/**
 * Evaluate mechanics for a card being played as `mode` (attack/defend),
 * filtered to entries whose condition is satisfied by the current `ctx`.
 *
 * `ctx.damageDealt` / `ctx.fullBlock` are only present in the resolveCombat
 * call site. On the play-time call site they're undefined and damage-gated
 * entries are skipped; resolveCombat re-runs evalMechanics with damage data.
 */
export function evalMechanics(
  card: Card,
  mode: "attack" | "defend",
  ctx: Ctx,
): MechanicResult {
  const result = emptyResult();
  const entries = readEntries(card);
  if (entries.length === 0) return result;

  const effectCtx: EffectCtx = {
    self: ctx.self,
    opponent: ctx.opponent,
    cardName: ctx.cardName,
    currentRound: ctx.currentRound,
    selfNum: ctx.selfNum,
  };

  // Phase detection: resolveCombat passes damage/fullBlock context, playAD/Equip/Sig do not.
  // Without this, `condition: none` entries would re-fire during combat resolution and
  // double-apply (e.g. Unbroken Sands queueing its cost-reduction buff twice).
  const isResolvePhase =
    ctx.damageDealt !== undefined ||
    ctx.damageTaken !== undefined ||
    ctx.fullBlock !== undefined;

  for (const m of entries) {
    if (!appliesWhenMatches(m, mode)) continue;
    if (!conditionMet(m.condition, ctx)) continue;
    // Play-phase: skip deferred (damage-gated) conditions — they fire later.
    // Resolve-phase: skip non-deferred (already fired at play time).
    if (isResolvePhase && !isDeferredCondition(m.condition)) continue;
    if (!isResolvePhase && isDeferredCondition(m.condition)) continue;
    for (const row of m.effects ?? []) {
      applyEffectRow(row, effectCtx, result);
    }
  }
  return result;
}

function readEntries(card: Card): MechanicEntry[] {
  const ad = migrateAttackDefendFields(card.attackDefendFields);
  return ad?.mechanics ?? [];
}

function appliesWhenMatches(
  m: MechanicEntry,
  mode: "attack" | "defend",
): boolean {
  if (m.appliesWhen === "either") return true;
  return m.appliesWhen === mode;
}

function conditionMet(c: MechanicCondition, ctx: Ctx): boolean {
  switch (c) {
    case "none":
      return true;
    case "attack-deals-damage":
      // Only true when caller passed damage data AND damage was dealt.
      return (ctx.damageDealt ?? 0) > 0;
    case "attack-fully-blocked":
      // Caller passes ctx.fullBlock=true on the attacker side when the block
      // fully neutralized damage. The resolveCombat caller passes the same
      // flag to both attack and defend mechanic evaluations.
      return Boolean(ctx.fullBlock);
    case "block-fully-blocks":
      return Boolean(ctx.fullBlock);
    default:
      return false;
  }
}

/**
 * True when an entry's condition needs combat data (damage / fullBlock) and
 * therefore must NOT fire on play — it must wait for resolveCombat.
 */
export function isDeferredCondition(c: MechanicCondition): boolean {
  return (
    c === "attack-deals-damage" ||
    c === "attack-fully-blocked" ||
    c === "block-fully-blocks"
  );
}
